/*
 * Cutlist Maker — plywood bookcase viewer (Three.js 0.186). TypeScript port of
 * design/shared/viewer.js, plus "Inspect finished model" (loadFinishedModel).
 *
 *   import { mountBookcase } from '@/viewer';
 *   const viewer = mountBookcase(hostElement, { theme: 'light' });
 *   viewer.update(build);           // build = deriveBuild(design) from @/domain
 *
 * World: metres, +x right, +y up, +z towards the viewer. The carcass is centred
 * on x and z, standing on the floor (y = 0). Panel placement comes from the core
 * in millimetres (origin floor/left/back), converted here.
 */
import * as THREE from 'three';
import type { Placement } from '@/domain';
import { thicknessesOf, type ViewerBuild as BuildModel } from './types';
import { OrbitControls } from './vendor/OrbitControls.js';
import { RoomEnvironment } from './vendor/RoomEnvironment.js';
import { GLTFLoader } from './vendor/GLTFLoader.js';
import { clamp01, rng, makeRadialCanvas, makeContactCanvas, makeCornerCanvas } from './textures';
import { basisFor, type BasisKey, type Box6 } from './panel';
import { Kit } from './kit/loader';
import { boardGeometryFor, buildJointLayer, displayPlacements, DEFAULT_EDGE_PROFILES, GLASS, type HardwareInstance, type JointLayer } from './kit/assemble';
import { materialsFor, resolvePartLook } from './kit/look';
import { mitreEnds, type MitreEnd } from './kit/joints';
import type { KitAssemblyInput, KitLod, PartLook } from './kit/types';
import { THEMES, isThemeName, makeAOUniforms, patchAO, MAX_H, BOOK_COLOURS, buildFigure, type ThemeName } from './materials';
import { collectPartNodes, partIdOf, disposeObject, materialList, texturesOf, isStagingNode } from './finished';
import type {
  BookcaseViewer,
  FinishedModelInfo,
  ScreenPosition,
  ScreenTarget,
  SnapshotOptions,
  ViewName,
  ViewerInset,
  ViewerOptions,
} from './types';

const MM = 0.001;
const ASSET = (p: string): string => `${import.meta.env.BASE_URL}${p}`;

const reducedMotion = (): boolean => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
const ease = (t: number): number => 1 - Math.pow(1 - t, 3);
const easeInOut = (t: number): number => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

type Vec3 = [number, number, number];
type TextureSource = 'loading' | 'kit' | 'fallback';
interface Dims {
  W: number;
  H: number;
  D: number;
  t: number;
  b: number;
}
interface PanelRecord {
  id: string;
  mesh: THREE.Mesh<THREE.BufferGeometry, THREE.Material[]>;
  geo: THREE.BufferGeometry;
  /** Kit board geometry key (size, look, LOD) and the box (m) it was built at; the mesh is scaled
   * from that box only while an edit animates, then rebuilt at the exact size. */
  geoKey: string;
  geoBox: Box6 | null;
  part: BuildModel['parts'][number] | null;
  look: PartLook | null;
  /** Mitred ends (thick frames), in the board's local frame. */
  mitres: MitreEnd[];
  basis: BasisKey;
  from: Box6 | null;
  to: Box6 | null;
  cur: Box6 | null;
  removing: boolean;
}
interface CamPose {
  p: THREE.Vector3;
  t: THREE.Vector3;
  fov: number;
}
interface OpeningRect {
  x0: number;
  x1: number;
  y0: number;
  y1: number;
  z0: number;
  z1: number;
}
interface Book {
  x: number;
  y: number;
  z: number;
  w: number;
  h: number;
  d: number;
  c: string;
  tilt: number;
  yaw?: number;
}
type HighlightKind = 'hover' | 'selected';
interface FinishedState {
  root: THREE.Group;
  partMeshes: Map<string, THREE.Mesh[]>;
  pickMeshes: THREE.Mesh[];
  original: Map<THREE.Mesh, THREE.Material | THREE.Material[]>;
  variants: Map<THREE.Material, Partial<Record<HighlightKind, THREE.Material>>>;
  boxes: Map<string, THREE.Box3>;
  roles: Map<string, string>;
  dims: Dims;
  textures: THREE.Texture[];
  info: FinishedModelInfo;
}
type Opts = ViewerOptions & { theme: ThemeName; view: ViewName; interactive: boolean };

function normInset(n: Partial<ViewerInset> | null | undefined): ViewerInset {
  const v = (x: unknown) => Math.max(0, Number(x) || 0);
  const s = n || {};
  return { top: v(s.top), right: v(s.right), bottom: v(s.bottom), left: v(s.left) };
}

/** Run `fn` on every mesh under `node`, skipping `staging.*` subtrees. */
function eachMesh(node: THREE.Object3D, fn: (m: THREE.Mesh) => void): void {
  if (isStagingNode(node)) return;
  if (node instanceof THREE.Mesh) fn(node);
  for (const c of node.children) eachMesh(c, fn);
}

// ---------------------------------------------------------------------------
// mountBookcase
// ---------------------------------------------------------------------------
export function mountBookcase(host: HTMLElement, opts: ViewerOptions = {}): BookcaseViewer {
  const o: Opts = {
    theme: 'light', background: 'transparent', showDims: false, scaleFigure: false, props: false, exploded: false,
    interactive: true, accent: null, onPick: null, onHover: null, onOpeningPick: null, onOpeningHover: null,
    onCameraChange: null, onContextLost: null, onReady: null, view: 'three-quarter',
    ...opts,
  };
  if (!isThemeName(o.theme)) o.theme = 'light';
  if (!o.view) o.view = 'three-quarter';
  if (o.interactive == null) o.interactive = true;
  if (getComputedStyle(host).position === 'static') host.style.position = 'relative';

  // --- state -----------------------------------------------------------------
  let build: BuildModel | null = null;
  let dims: Dims = { W: 0.9, H: 1.8, D: 0.32, t: 0.018, b: 0.006 }; // displayed (animated)
  let targetDims: Dims = { ...dims };
  let fromDims: Dims = { ...dims };
  let anim: { t0: number; dur: number } | null = null;
  let selected: string | null = null,
    hovered: string | null = null,
    openingHL: number | null = null,
    openingHover: number | null = null;
  let disposed = false,
    needsRender = true,
    raf = 0,
    lost = false;
  let theme: ThemeName = o.theme;
  let currentView: ViewName = o.view;
  let arrived = false; // the one-time arrival orbit (arrivalOrbit)
  let userMoved = false;
  let camTween: { t0: number; dur: number; from: CamPose; to: CamPose } | null = null;
  let lastFrameMs = 0,
    lastUpdateMs = 0;
  let propsSignature = '';
  const frameSubs = new Set<() => void>();
  const propMeshes: THREE.Object3D[] = [];
  let hostW = 1,
    hostH = 1;
  // Framing inset (px): overlays sitting on the stage. The model is fitted inside the
  // inner rectangle; the canvas still covers the whole host (camera view offset).
  let inset: ViewerInset = normInset(opts.inset);
  let snapshotting = false;
  let resizePending = false;
  let snapshotQuality: 'preview' | 'finished' = 'preview';
  let propsView: ViewName | null = null; // props are hidden in the edge-detail close-up
  let finished: FinishedState | null = null;
  let loadSeq = 0;

  function invalidate() {
    needsRender = true;
  }

  // --- renderer -------------------------------------------------------------
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance' });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.AgXToneMapping;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFShadowMap;
  const canvas = renderer.domElement;
  // Software rasterisers (headless SwiftShader) get fewer finished-render passes.
  const software = (() => {
    try {
      const gl = renderer.getContext() as WebGL2RenderingContext;
      const ext = gl.getExtension('WEBGL_debug_renderer_info');
      return /swiftshader|llvmpipe|software/i.test(String(gl.getParameter(ext ? ext.UNMASKED_RENDERER_WEBGL : gl.RENDERER)));
    } catch {
      return false;
    }
  })();
  canvas.style.cssText = 'display:block;width:100%;height:100%;touch-action:none;outline:none;';
  canvas.setAttribute('aria-hidden', 'true');
  host.append(canvas);

  const overlay = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  overlay.setAttribute('aria-hidden', 'true');
  overlay.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;pointer-events:none;overflow:visible;font:inherit;font-variant-numeric:tabular-nums;';
  host.append(overlay);
  const notice = document.createElement('div');
  notice.style.cssText = 'position:absolute;inset:auto 0 12px 0;margin:auto;width:max-content;max-width:90%;padding:8px 12px;border-radius:8px;font:inherit;font-size:13px;background:rgba(0,0,0,.65);color:#fff;display:none;';
  host.append(notice);

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(30, 1, 0.02, 60);
  camera.position.set(1.8, 1.4, 3.6);

  // --- environment and lights ------------------------------------------------
  let envRT: THREE.WebGLRenderTarget | null = null;
  const makeEnv = () => {
    const pmrem = new THREE.PMREMGenerator(renderer);
    const room = new RoomEnvironment();
    envRT?.dispose();
    envRT = pmrem.fromScene(room, 0.04);
    room.dispose();
    pmrem.dispose();
    scene.environment = envRT.texture;
  };
  makeEnv();

  const key = new THREE.DirectionalLight(0xffffff, 2.6);
  key.castShadow = true;
  key.shadow.mapSize.set(2048, 2048);
  key.shadow.bias = -0.0004;
  key.shadow.normalBias = 0.003;
  key.shadow.radius = 9;
  key.shadow.blurSamples = 16;
  Object.assign(key.shadow.camera, { left: -1.8, right: 1.8, top: 2.6, bottom: -0.4, near: 0.5, far: 14 });
  scene.add(key, key.target);
  // finished renders use a soft spot (light pool + falloff) instead of the directional key
  const spot = new THREE.SpotLight(0xffffff, 0, 0, 0.4, 1, 0);
  spot.castShadow = true;
  spot.shadow.mapSize.set(2048, 2048);
  spot.shadow.bias = -0.0003;
  spot.shadow.normalBias = 0.0025;
  spot.shadow.radius = 1.5;
  spot.visible = false;
  scene.add(spot, spot.target);
  const fill = new THREE.DirectionalLight(0xffffff, 0.3);
  scene.add(fill, fill.target);

  // floor: shadow catcher + soft contact shadow + optional warm glow
  const floorGeo = new THREE.PlaneGeometry(30, 30);
  const floorMat = new THREE.ShadowMaterial({ color: 0x0b2147, opacity: 0.2, transparent: true, depthWrite: false });
  const floor = new THREE.Mesh(floorGeo, floorMat);
  floor.rotation.x = -Math.PI / 2;
  floor.receiveShadow = true;
  floor.renderOrder = -2;
  scene.add(floor);
  const contactTex = new THREE.CanvasTexture(makeContactCanvas());
  const contactMat = new THREE.MeshBasicMaterial({ color: 0x041e45, alphaMap: contactTex, transparent: true, opacity: 0.3, depthWrite: false });
  const unitPlane = new THREE.PlaneGeometry(1, 1);
  const contact = new THREE.Mesh(unitPlane, contactMat);
  contact.rotation.x = -Math.PI / 2;
  contact.position.y = 0.0004;
  contact.renderOrder = -1;
  scene.add(contact);
  const glowTex = new THREE.CanvasTexture(makeRadialCanvas(256, 0, [[0, 1], [0.45, 0.45], [1, 0]]));
  const glowMat = new THREE.MeshBasicMaterial({ color: 0xffb067, alphaMap: glowTex, transparent: true, opacity: 0, depthWrite: false, toneMapped: false });
  const glow = new THREE.Mesh(unitPlane, glowMat);
  glow.rotation.x = -Math.PI / 2;
  glow.position.y = 0.0002;
  glow.renderOrder = -3;
  scene.add(glow);

  // backdrop (wall + floor) for finished snapshots or live staging
  const backdrop = new THREE.Group();
  const wallMat = new THREE.MeshStandardMaterial({ color: 0xeef2f6, roughness: 0.95 });
  const roomFloorMat = new THREE.MeshStandardMaterial({ color: 0xdde3ea, roughness: 0.9 });
  const wall = new THREE.Mesh(floorGeo, wallMat);
  wall.receiveShadow = true;
  const roomFloor = new THREE.Mesh(floorGeo, roomFloorMat);
  roomFloor.rotation.x = -Math.PI / 2;
  roomFloor.receiveShadow = true;
  roomFloor.position.y = -0.0002;
  const wallContactGeo = new THREE.PlaneGeometry(1, 1);
  const wallContactMat = new THREE.MeshBasicMaterial({ color: 0x041e45, alphaMap: contactTex, transparent: true, opacity: 0.22, depthWrite: false });
  const wallContact = new THREE.Mesh(wallContactGeo, wallContactMat);
  const cornerTex = new THREE.CanvasTexture(makeCornerCanvas());
  const cornerMat = new THREE.MeshBasicMaterial({ color: 0x000000, alphaMap: cornerTex, transparent: true, opacity: 0.12, depthWrite: false });
  const wallCorner = new THREE.Mesh(wallContactGeo, cornerMat); // on the wall, dark at the floor
  wallCorner.rotation.z = Math.PI; // gradient: dark at the bottom
  const floorCorner = new THREE.Mesh(wallContactGeo, cornerMat); // on the floor, dark at the wall
  floorCorner.rotation.x = -Math.PI / 2;
  backdrop.add(wall, roomFloor, wallContact, wallCorner, floorCorner);
  backdrop.visible = false;
  scene.add(backdrop);

  // --- materials (the Blender kit, PLAN §6) ----------------------------------------
  // Every part's face, edge and end materials come from the kit by its stockId and material
  // (birch ply, sheesham, oak); the finish from `appearance`. Until the kit's maps arrive (or if
  // they cannot load) parts show a flat colour of their species.
  const aoU = makeAOUniforms();
  let kit: Kit | null = null;
  let textureSource: TextureSource = 'loading';
  const patched = new WeakSet<THREE.Material>();
  const patch = <M extends THREE.Material>(m: M): M => {
    if (!patched.has(m)) {
      patchAO(m, aoU);
      patched.add(m);
    }
    return m;
  };
  const FALLBACK_SRGB: Array<[RegExp, string]> = [[/sheesham/, '#9a5a30'], [/oak/, '#c49b6b'], [/steel|brass|black|slot/, '#6b6b6b'], [/./, '#e2c99e']];
  const fallbackMats = new Map<string, THREE.MeshPhysicalMaterial>();
  const fallbackMat = (id: string) => {
    let m = fallbackMats.get(id);
    if (!m) {
      const hex = FALLBACK_SRGB.find(([re]) => re.test(id))![1];
      m = patch(new THREE.MeshPhysicalMaterial({ color: new THREE.Color(hex), roughness: 0.62, clearcoat: 0.15, clearcoatRoughness: 0.5, vertexColors: true, name: `fallback:${id}` }));
      fallbackMats.set(id, m);
    }
    return m;
  };
  const variants = new Map<THREE.Material, Partial<Record<HighlightKind, THREE.Material>>>();
  /** Kit material (or its flat fallback) for a part state; hover/selected variants share programs. */
  function kitMat(id: string, finish: string, kind: 'normal' | HighlightKind = 'normal'): THREE.Material {
    return variantOf(kit && kit.hasMaterial(id) ? patch(kit.material(id, finish)) : fallbackMat(id), kind);
  }
  /** A material as drawn hovered or selected (the variants share the base's program). */
  function variantOf(base: THREE.Material, kind: 'normal' | HighlightKind): THREE.Material {
    if (kind === 'normal') return base;
    const rec = variants.get(base) ?? {};
    let v = rec[kind];
    if (!v) {
      v = base.clone();
      v.name = `${base.name}-${kind}`;
      patchAO(v, aoU);
      themeHighlight(v, kind);
      rec[kind] = v;
      variants.set(base, rec);
    }
    return v;
  }
  /** The piece's finish: the user's choice, else the stock's own (solid timber oiled, ply matt lacquer). */
  function finishFor(): string {
    const chosen = o.appearance?.finishId;
    if (chosen) return chosen;
    for (const r of panels.values()) if (r.look && r.look.edgeUv === 'metres') return 'oiled';
    return 'matt';
  }
  const lodNow = (): KitLod => (o.detail || explodeTo > 0.5 ? 'inspect' : 'live');

  let resolveReady: () => void = () => {};
  const ready = new Promise<void>((res) => (resolveReady = res));
  void Kit.load({ renderer, baseUrl: ASSET('kit/') })
    .then(async (k) => {
      if (disposed) {
        k.dispose();
        return;
      }
      kit = k;
      await ensureKitMaterials();
      textureSource = 'kit';
    })
    .catch((err: unknown) => {
      console.warn('Kit unavailable; showing flat materials.', err);
      textureSource = 'fallback';
    })
    .finally(() => {
      if (disposed) return resolveReady();
      rebuildAllGeometry();
      refreshMaterials();
      scheduleLayer(0);
      resolveReady();
    });
  /** Load the maps the current parts (and their fixings) need, then show them. */
  async function ensureKitMaterials(): Promise<void> {
    if (!kit || !build) return;
    const looks = [...panels.values()].map((r) => r.look).filter((l): l is PartLook => !!l);
    const ids = materialsFor(looks);
    if (ids.every((id) => kit!.hasMaterial(id))) return;
    await kit.ensureMaterials(ids);
    if (disposed) return;
    refreshMaterials();
    scheduleLayer(0);
  }

  const figureMat = new THREE.MeshStandardMaterial({ color: 0xc9d1dc, roughness: 0.92 });
  const highlightMat = new THREE.MeshBasicMaterial({ color: 0x0756bf, transparent: true, opacity: 0.2, depthWrite: false, toneMapped: false });
  const highlightLineMat = new THREE.LineBasicMaterial({ color: 0x0756bf, transparent: true, opacity: 0.9, depthTest: false, toneMapped: false });
  const outlineMat = new THREE.LineBasicMaterial({ color: 0x0756bf, transparent: true, opacity: 1, toneMapped: false });
  const selShellMat = new THREE.MeshBasicMaterial({ color: 0x0756bf, transparent: true, opacity: 0.26, depthWrite: false, toneMapped: false });
  const hovShellMat = new THREE.MeshBasicMaterial({ color: 0x0756bf, transparent: true, opacity: 0.12, depthWrite: false, toneMapped: false });
  const bookMat = new THREE.MeshStandardMaterial({ roughness: 0.82, metalness: 0 });
  const ceramicMat = new THREE.MeshPhysicalMaterial({ color: 0xf1f3f6, roughness: 0.35, clearcoat: 0.6, clearcoatRoughness: 0.25 });
  const ceramicDarkMat = new THREE.MeshStandardMaterial({ color: 0x4a4440, roughness: 0.7 });

  // --- carcass group ----------------------------------------------------------
  const carcass = new THREE.Group();
  carcass.name = 'carcass';
  scene.add(carcass);
  const panels = new Map<string, PanelRecord>();
  const unitBox = new THREE.BoxGeometry(1, 1, 1);
  const unitEdges = new THREE.EdgesGeometry(unitBox);
  const openingBox = new THREE.Mesh(unitBox, highlightMat);
  const openingLines = new THREE.LineSegments(unitEdges, highlightLineMat);
  openingBox.visible = openingLines.visible = false;
  openingBox.renderOrder = openingLines.renderOrder = 5;
  carcass.add(openingBox, openingLines);
  const outline = new THREE.LineSegments(unitEdges, outlineMat);
  outline.visible = false;
  outline.renderOrder = 7;
  const selShell = new THREE.Mesh(unitBox, selShellMat);
  selShell.visible = false;
  selShell.renderOrder = 6;
  const hovShell = new THREE.Mesh(unitBox, hovShellMat);
  hovShell.visible = false;
  hovShell.renderOrder = 6;
  carcass.add(outline, selShell, hovShell);
  const { group: figure, geometries: figureGeos } = buildFigure(figureMat);
  figure.visible = false;
  scene.add(figure);
  const propsGroup = new THREE.Group();
  propsGroup.name = 'props';
  carcass.add(propsGroup);

  // --- kit tier 2: joint details and fixings ---------------------------------------
  // Rebuilt after an edit settles (never on the edit's own frame), from the kit pieces already in
  // memory: nothing is reloaded. Details show on the assembled model; in Inspect (exploded) the
  // joints are apart, so only the fixings show, moved with their parts.
  const layerGroup = new THREE.Group();
  layerGroup.name = 'kit-details';
  layerGroup.visible = false;
  carcass.add(layerGroup);
  let layer: JointLayer | null = null;
  let layerTimer: ReturnType<typeof setTimeout> | null = null;
  const kitInput = (b: BuildModel): KitAssemblyInput => ({ parts: b.parts, joints: b.joints ?? [], hardware: b.hardware ?? [] });
  function scheduleLayer(delay = 140) {
    if (layerTimer) clearTimeout(layerTimer);
    layerTimer = setTimeout(() => {
      layerTimer = null;
      if (disposed) return;
      if (anim) return scheduleLayer(60);
      buildLayer();
    }, delay);
  }
  function buildLayer() {
    if (!kit || !build) return;
    const looks = new Map<string, PartLook>();
    for (const r of panels.values()) if (r.look && !r.removing) looks.set(r.id, r.look);
    if (![...materialsFor(looks.values())].every((id) => kit!.hasMaterial(id))) return; // ensureKitMaterials reschedules
    layer?.dispose();
    layerGroup.clear();
    const lod = lodNow();
    layer = buildJointLayer(kit, kitInput(build), { lod, finish: finishFor(), looks, includeHiddenHardware: true });
    for (const m of layer.details) {
      patch(m.material as THREE.Material);
      layerGroup.add(m);
    }
    for (const h of layer.hardware) {
      patch(h.material as THREE.Material);
      h.userData.base = (h.userData.instances as HardwareInstance[]).map((i) => i.matrix.clone());
      layerGroup.add(h);
    }
    placeLayer();
    layerGroup.visible = true;
    invalidate();
  }
  /** Details only on the assembled model; fixings (when shown) follow their parts' explode offset. */
  function placeLayer() {
    if (!layer) return;
    const assembled = explodeT < 0.01;
    for (const m of layer.details) m.visible = assembled;
    const show = !!o.appearance?.showHardware;
    const m = new THREE.Matrix4();
    for (const h of layer.hardware) {
      // "Show fixings" hides only the fixings; feet and legs always render (they are the piece's base)
      const on = show || !!h.userData.structural;
      h.visible = on;
      if (!on) continue;
      const list = h.userData.instances as HardwareInstance[];
      const base = h.userData.base as THREE.Matrix4[];
      list.forEach((inst, i) => {
        const ex = explodeFor(inst.partId);
        m.copy(base[i]);
        const om = openMatrix(inst.partId);
        if (om) m.premultiply(om); // the layer shares the panels' carcass frame
        m.premultiply(new THREE.Matrix4().makeTranslation(ex[0] * explodeT, ex[1] * explodeT, ex[2] * explodeT));
        h.setMatrixAt(i, m);
      });
      h.instanceMatrix.needsUpdate = true;
      h.computeBoundingSphere();
    }
  }

  // finished-model highlights (world space; same geometry and accent materials as the live ones)
  const finishedHL = new THREE.Group();
  finishedHL.name = 'finished-highlights';
  finishedHL.visible = false;
  const fOutline = new THREE.LineSegments(unitEdges, outlineMat);
  fOutline.renderOrder = 7;
  const fSelShell = new THREE.Mesh(unitBox, selShellMat);
  fSelShell.renderOrder = 6;
  const fHovShell = new THREE.Mesh(unitBox, hovShellMat);
  fHovShell.renderOrder = 6;
  fOutline.visible = fSelShell.visible = fHovShell.visible = false;
  finishedHL.add(fOutline, fSelShell, fHovShell);
  scene.add(finishedHL);

  const EXPLODE: Record<string, Vec3> = {
    'side-l': [-0.14, 0, 0], 'side-r': [0.14, 0, 0], top: [0, 0.14, 0], bottom: [0, 0, 0.05], back: [0, 0, -0.18],
  };
  const explodeFor = (id: string): Vec3 =>
    EXPLODE[id] ||
    (id.startsWith('door-') || id.startsWith('slider-') ? [0, 0, 0.24] : id.startsWith('drawer-') ? [0, 0, 0.32] : id.startsWith('shelf-') ? [0, 0, 0.09] : id.startsWith('back') ? [0, 0, -0.18] : id.startsWith('divider') || id.startsWith('rowdiv') ? [0, 0.07, 0.05] : id === 'plinth' ? [0, -0.0, 0.16] : [0, 0, 0]);
  let explodeT = o.exploded ? 1 : 0,
    explodeFrom = explodeT,
    explodeTo = explodeT;
  // doors and drawers opened (Look "Open fronts"): appearance only, never in the design or its hash
  let openT = o.frontsOpen ? 1 : 0,
    openFrom = openT,
    openTo = openT;
  /** The door leaf, drawer or sliding panel a front part moves with (src/families/fronts.ts ids). */
  const frontGroup = (id: string): string | null =>
    id.startsWith('door-') ? id.replace(/-(stile-[lr]|rail-[tb]|glass)$/, '') : id.startsWith('drawer-') ? id.replace(/-(front|side-[lr]|rear|sub|bottom)$/, '') : id.startsWith('slider-') ? id : null;
  /**
   * How a front part moves when opened (metres, carcass frame): a door swings 100° about its hinge edge
   * (the board it hangs from tells the side), a drawer slides out by most of its depth, the first sliding
   * panel slides across the second.
   */
  function frontMotion(id: string): { pivot: [number, number]; angle: number; dx: number; dz: number } | null {
    const g = frontGroup(id);
    if (!g || !build) return null;
    const members = build.parts.filter((p) => frontGroup(p.id) === g);
    if (!members.length) return null;
    const x0 = Math.min(...members.map((p) => p.placement.x)) * MM, x1 = Math.max(...members.map((p) => p.placement.x + p.placement.sx)) * MM;
    const z1 = Math.max(...members.map((p) => p.placement.z + p.placement.sz)) * MM, z0 = Math.min(...members.map((p) => p.placement.z)) * MM;
    if (g.startsWith('door-')) {
      const hinge = (build.joints ?? []).find((j) => j.kind === 'hinge' && frontGroup(j.a) === g);
      const board = hinge && build.parts.find((p) => p.id === hinge.b);
      const left = board ? board.placement.x + board.placement.sx / 2 < (x0 + x1) / 2 / MM : true;
      const a = (100 * Math.PI) / 180;
      return left ? { pivot: [x0, z1], angle: -a, dx: 0, dz: 0 } : { pivot: [x1, z1], angle: a, dx: 0, dz: 0 };
    }
    if (g.startsWith('drawer-')) return { pivot: [0, 0], angle: 0, dx: 0, dz: (z1 - z0) * 0.7 };
    return { pivot: [0, 0], angle: 0, dx: /-1$/.test(g) ? (x1 - x0) * 0.45 : 0, dz: 0 };
  }
  /** The open-front transform for a part at `openT` (identity when closed or not a front). */
  function openMatrix(id: string): THREE.Matrix4 | null {
    if (openT < 0.001) return null;
    const f = frontMotion(id);
    if (!f) return null;
    const m = new THREE.Matrix4().makeTranslation(f.dx * openT, 0, f.dz * openT);
    if (f.angle) {
      const [px, pz] = f.pivot;
      m.multiply(new THREE.Matrix4().makeTranslation(px, 0, pz)).multiply(new THREE.Matrix4().makeRotationY(f.angle * openT)).multiply(new THREE.Matrix4().makeTranslation(-px, 0, -pz));
    }
    return m;
  }

  // --- controls ----------------------------------------------------------------
  let controls: OrbitControls | null = null;
  if (o.interactive) {
    const c = new OrbitControls(camera, canvas);
    c.enableDamping = true;
    c.dampingFactor = 0.09;
    c.minDistance = 0.18;
    c.maxDistance = 12;
    c.maxPolarAngle = Math.PI / 2 - 0.035;
    c.minPolarAngle = 0.08;
    c.screenSpacePanning = true;
    c.zoomToCursor = true;
    c.addEventListener('start', () => {
      userMoved = true;
      camTween = null;
    });
    c.addEventListener('change', () => {
      if (c.target.y < 0.02) c.target.y = 0.02;
      invalidate();
      o.onCameraChange?.();
    });
    controls = c;
  }

  // --- panels -------------------------------------------------------------------
  function panelRecord(part: BuildModel['parts'][number]): PanelRecord {
    let r = panels.get(part.id);
    if (r) return r;
    const geo = new THREE.BufferGeometry();
    const mesh = new THREE.Mesh<THREE.BufferGeometry, THREE.Material[]>(geo, []);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.name = part.id;
    mesh.userData.partId = part.id;
    r = {
      id: part.id, mesh, geo, geoKey: '', geoBox: null, part, look: null, mitres: [], basis: basisFor(part),
      from: null, to: null, cur: null, removing: false,
    };
    carcass.add(mesh);
    panels.set(part.id, r);
    return r;
  }

  /** Kit board geometry at the record's target box (exact size, physical-scale grain); reused while
   * its size, look and LOD are unchanged, so an edit only rebuilds the boards it changed. */
  function ensureGeometry(r: PanelRecord) {
    if (!r.part || !r.look || !r.to) return;
    const box = r.to;
    const placement = { x: box.x / MM, y: box.y / MM, z: box.z / MM, sx: box.sx / MM, sy: box.sy / MM, sz: box.sz / MM };
    const profiles = kit?.manifest.edgeProfiles ?? DEFAULT_EDGE_PROFILES;
    const built = boardGeometryFor(profiles, r.part, r.look, lodNow(), placement, r.mitres);
    if (built.key === r.geoKey) {
      built.geometry.dispose();
      return;
    }
    r.geo.dispose();
    r.geo = built.geometry;
    r.mesh.geometry = built.geometry;
    r.geoKey = built.key;
    r.geoBox = { ...box };
  }
  function rebuildAllGeometry() {
    for (const r of panels.values()) if (!r.removing) ensureGeometry(r);
    invalidate();
  }

  function applyPanel(r: PanelRecord, p: Box6, exT: number) {
    // p: {x,y,z,sx,sy,sz} metres (min corner + size); the geometry is centred on its build box
    const g = r.geoBox;
    if (g) r.mesh.scale.set(g.sx > 0 ? p.sx / g.sx : 1, g.sy > 0 ? p.sy / g.sy : 1, g.sz > 0 ? p.sz / g.sz : 1);
    const ex = explodeFor(r.id);
    r.mesh.position.set(p.x + p.sx / 2 + ex[0] * exT, p.y + p.sy / 2 + ex[1] * exT, p.z + p.sz / 2 + ex[2] * exT);
    r.mesh.rotation.set(0, 0, 0);
    const om = openMatrix(r.id);
    if (om) { r.mesh.position.applyMatrix4(om); r.mesh.rotation.setFromRotationMatrix(om); }
    r.cur = p;
  }

  const toM = (pl: Placement): Box6 => ({ x: pl.x * MM, y: pl.y * MM, z: pl.z * MM, sx: pl.sx * MM, sy: pl.sy * MM, sz: pl.sz * MM });
  const lerpP = (a: Box6, b: Box6, t: number): Box6 => ({
    x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, z: a.z + (b.z - a.z) * t,
    sx: a.sx + (b.sx - a.sx) * t, sy: a.sy + (b.sy - a.sy) * t, sz: a.sz + (b.sz - a.sz) * t,
  });
  const lerpD = (a: Dims, b: Dims, t: number): Dims => ({
    W: a.W + (b.W - a.W) * t, H: a.H + (b.H - a.H) * t, D: a.D + (b.D - a.D) * t, t: a.t + (b.t - a.t) * t, b: a.b + (b.b - a.b) * t,
  });

  function panelMaterials(r: PanelRecord): THREE.Material[] {
    const kind = r.id === selected ? 'selected' : r.id === hovered ? 'hover' : 'normal';
    const look = r.look;
    if (!look) return [fallbackMat('x'), fallbackMat('x'), fallbackMat('x')];
    // glass in a glazed door (src/families/fronts.ts) is never a timber
    if (r.part?.role === 'glass') return [GLASS, GLASS, GLASS].map((m) => variantOf(m, kind));
    const f = finishFor();
    return [kitMat(look.face, f, kind), kitMat(look.edge, f, kind), kitMat(look.end, f, kind)];
  }
  function refreshMaterials() {
    for (const r of panels.values()) r.mesh.material = panelMaterials(r);
    refreshFinishedMaterials();
    invalidate();
  }
  /** Is `id` a pickable part of what is shown (finished model or live panels)? */
  function hasPart(id: string): boolean {
    return finished ? finished.partMeshes.has(id) : panels.has(id);
  }

  // --- update(build) ------------------------------------------------------------
  function update(nextBuild: BuildModel, { animate = true }: { animate?: boolean } = {}): number {
    const t0 = performance.now();
    build = nextBuild;
    const t = thicknessesOf(nextBuild).carcass * MM,
      b = thicknessesOf(nextBuild).back * MM;
    const doAnim = animate && !reducedMotion() && panels.size > 0;
    const now = performance.now();
    // freeze current interpolated state as "from"
    fromDims = { ...dims };
    targetDims = { W: nextBuild.envelope.W * MM, H: nextBuild.envelope.H * MM, D: nextBuild.envelope.D * MM, t, b };
    const ids = new Set<string>();
    const looks = new Map(nextBuild.parts.map((p) => [p.id, resolvePartLook(p)]));
    const shown = displayPlacements(kit, kitInput(nextBuild), looks);
    const mitres = mitreEnds(kitInput(nextBuild), shown);
    for (const part of nextBuild.parts) {
      ids.add(part.id);
      const r = panelRecord(part);
      r.basis = basisFor(part);
      r.part = part;
      r.look = looks.get(part.id) ?? null;
      r.mitres = mitres.get(part.id) ?? [];
      const to = toM(shown.get(part.id) ?? part.placement);
      if (!r.cur) {
        // new panel: grow from its centre along the length
        if (doAnim) {
          const c = { ...to };
          if (r.basis === 'horizontal') {
            c.x = to.x + to.sx / 2;
            c.sx = 0.0001;
          } else {
            c.y = to.y + to.sy / 2;
            c.sy = 0.0001;
          }
          r.from = c;
        } else r.from = to;
      } else r.from = { ...r.cur };
      r.to = to;
      r.removing = false;
      ensureGeometry(r);
    }
    for (const r of [...panels.values()]) {
      if (!ids.has(r.id) && r.cur) {
        r.removing = true;
        r.from = { ...r.cur };
        r.to = r.basis === 'horizontal' ? { ...r.cur, x: r.cur.x + r.cur.sx / 2, sx: 0.0001 } : { ...r.cur, y: r.cur.y + r.cur.sy / 2, sy: 0.0001 };
        if (!doAnim) removePanel(r);
      } else if (!ids.has(r.id)) removePanel(r);
    }
    if (selected && !hasPart(selected)) selected = null;
    if (hovered && !hasPart(hovered)) hovered = null;
    refreshMaterials();
    void ensureKitMaterials();
    layerGroup.visible = false; // details and fixings follow once the edit settles
    scheduleLayer();
    if (doAnim) anim = { t0: now, dur: 260 };
    else {
      anim = null;
      step(1);
    }
    if (!doAnim) rebuildProps();
    if (!userMoved && !camTween && !arrivalOrbit()) frameView(currentView, { animate: doAnim, duration: 420 });
    lastUpdateMs = performance.now() - t0;
    invalidate();
    return lastUpdateMs;
  }

  function removePanel(r: PanelRecord) {
    carcass.remove(r.mesh);
    r.geo.dispose();
    panels.delete(r.id);
  }

  function step(k: number) {
    const e = ease(clamp01(k));
    dims = lerpD(fromDims, targetDims, e);
    explodeT = explodeFrom + (explodeTo - explodeFrom) * e;
    openT = openFrom + (openTo - openFrom) * e;
    for (const r of [...panels.values()]) {
      if (!r.to || !r.from) continue;
      applyPanel(r, lerpP(r.from, r.to, e), explodeT);
      if (k >= 1) {
        r.from = r.to;
        if (r.removing) removePanel(r);
      }
    }
    carcass.position.set(-dims.W / 2, 0, -dims.D / 2);
    placeLayer();
    // AO uniforms from the (interpolated) horizontal panels
    aoU.uCarcass.value.set(dims.W, dims.H, dims.D, dims.t);
    aoU.uBack.value = dims.b;
    aoU.uOrigin.value.set(-dims.W / 2, 0, -dims.D / 2);
    let i = 0;
    for (const r of panels.values()) {
      if (r.basis !== 'horizontal' || !r.cur || r.removing || i >= MAX_H) continue;
      aoU.uHoriz.value[i++].set(r.cur.y, r.cur.y + r.cur.sy);
    }
    aoU.uHCount.value = i;
    aoU.uAO.value.x = (explodeT > 0.01 ? 0 : 1) * (snapshotQuality === 'finished' ? 1 : 0.62);
    placeStage();
    placeHighlights();
    placeProps();
  }

  /** Dimensions of what is shown: the finished model's box when loaded, else the live carcass. */
  function stageDims(): Dims {
    return finished ? finished.dims : dims;
  }
  /** Framing target: the finished box, else the live carcass's target (end-of-animation) size. */
  function frameDims(): Dims {
    return finished ? finished.dims : targetDims;
  }

  // contact shadow, glow, figure, backdrop and shadow camera follow the footprint
  function placeStage() {
    const sd = stageDims();
    contact.scale.set(sd.W + 0.16, sd.D + 0.16, 1);
    contact.position.z = 0;
    glow.scale.set(Math.max(2.4, sd.W * 3), Math.max(2.4, sd.W * 3), 1);
    glow.position.z = 0.25;
    figure.position.set(-(sd.W / 2 + 0.78), 0, -0.04);
    placeBackdrop(sd);
    fitShadowCamera(sd);
  }

  function placeBackdrop(sd: Dims) {
    const zw = -sd.D / 2 - 0.004;
    wall.position.set(0, 0, zw);
    roomFloor.position.set(0, -0.0002, 0);
    wallContact.scale.set(sd.W + 0.1, sd.H + 0.08, 1);
    wallContact.position.set(0, sd.H / 2, zw + 0.0008);
    wallCorner.scale.set(30, 0.28, 1);
    wallCorner.position.set(0, 0.14, zw + 0.0006);
    floorCorner.scale.set(30, 0.3, 1);
    floorCorner.position.set(0, 0.0003, zw + 0.15);
  }

  function fitShadowCamera(sd: Dims) {
    const w = sd.W / 2 + (figure.visible ? 1.1 : 0.35);
    const target = new THREE.Vector3(0, sd.H / 2, 0);
    key.target.position.copy(target);
    key.position.copy(target).add(new THREE.Vector3(-2.1, 3.9, 2.9));
    spot.target.position.set(0, sd.H * 0.42, 0);
    spot.position.copy(spot.target.position).add(new THREE.Vector3(-1.9, 3.3, 3.1));
    fill.target.position.copy(target);
    fill.position.copy(target).add(new THREE.Vector3(3, 0.6, 2));
    const r = Math.max(w, sd.H / 2 + 0.4, 1.0) * 1.15;
    Object.assign(key.shadow.camera, { left: -r, right: r, top: r, bottom: -r });
    key.shadow.camera.updateProjectionMatrix();
  }

  // --- highlights ----------------------------------------------------------------
  /** Opening `i` of the BoardAssembly (any family, any bay), from its bounds (mm). */
  function openingRect(i: number): OpeningRect | null {
    if (!build || !build.openings[i]) return null;
    const bd = build.openings[i].bounds;
    return { x0: bd.x0 * MM, x1: bd.x1 * MM, y0: bd.y0 * MM, y1: bd.y1 * MM, z0: dims.b, z1: dims.D };
  }

  function placeHighlights() {
    const idx = openingHL ?? openingHover;
    const rect = idx != null ? openingRect(idx) : null;
    if (rect && explodeT < 0.01) {
      const e = 0.0008;
      const sx = rect.x1 - rect.x0 - 2 * e,
        sy = Math.max(0.001, rect.y1 - rect.y0 - 2 * e),
        sz = rect.z1 - rect.z0 - e;
      for (const m of [openingBox, openingLines]) {
        m.visible = true;
        m.scale.set(sx, sy, sz);
        m.position.set((rect.x0 + rect.x1) / 2, (rect.y0 + rect.y1) / 2, (rect.z0 + rect.z1) / 2);
      }
      highlightMat.opacity = openingHL != null ? 0.2 : 0.1;
      highlightLineMat.opacity = openingHL != null ? 0.9 : 0.45;
    } else {
      openingBox.visible = openingLines.visible = false;
    }
    const shell = (obj: THREE.Object3D, id: string | null, e: number) => {
      const r = id ? panels.get(id) : null;
      if (!r?.cur) {
        obj.visible = false;
        return;
      }
      const ex = explodeFor(r.id);
      obj.visible = true;
      obj.scale.set(r.cur.sx + 2 * e, r.cur.sy + 2 * e, r.cur.sz + 2 * e);
      obj.position.set(r.cur.x + r.cur.sx / 2 + ex[0] * explodeT, r.cur.y + r.cur.sy / 2 + ex[1] * explodeT, r.cur.z + r.cur.sz / 2 + ex[2] * explodeT);
    };
    shell(outline, selected, 0.0016);
    shell(selShell, selected, 0.0012);
    shell(hovShell, hovered !== selected ? hovered : null, 0.0012);
    // finished model: the same shells around the part's world box
    const fShell = (obj: THREE.Object3D, id: string | null, e: number) => {
      const box = id && finished ? finished.boxes.get(id) : null;
      if (!box) {
        obj.visible = false;
        return;
      }
      const s = box.getSize(new THREE.Vector3());
      const c = box.getCenter(new THREE.Vector3());
      obj.visible = true;
      obj.scale.set(s.x + 2 * e, s.y + 2 * e, s.z + 2 * e);
      obj.position.copy(c);
    };
    fShell(fOutline, selected, 0.0016);
    fShell(fSelShell, selected, 0.0012);
    fShell(fHovShell, hovered !== selected ? hovered : null, 0.0012);
    invalidate();
  }

  // --- props (books and a few objects) ----------------------------------------------
  function clearProps() {
    for (const g of [...propsGroup.children]) {
      g.traverse((m) => {
        if (m instanceof THREE.InstancedMesh) m.dispose();
      });
      propsGroup.remove(g);
    }
    propMeshes.length = 0;
  }
  const vaseGeo = new THREE.LatheGeometry(
    (
      [
        [0, 0], [0.038, 0], [0.046, 0.012], [0.05, 0.05], [0.044, 0.11], [0.026, 0.15], [0.022, 0.175], [0.027, 0.19], [0.023, 0.19], [0.018, 0.17], [0, 0.17],
      ] as Array<[number, number]>
    ).map(([x, y]) => new THREE.Vector2(x, y)),
    28,
  );
  const bowlGeo = new THREE.SphereGeometry(0.075, 28, 12, 0, Math.PI * 2, Math.PI / 2, Math.PI / 2);
  bowlGeo.rotateX(Math.PI);
  bowlGeo.translate(0, 0.075, 0);
  const bowlScale = new THREE.Vector3(1, 0.55, 1);

  /** Solid fronts hide what is behind them; glass does not (src/families/fronts.ts roles). */
  // glazed doors (stiles, rails and glass) never hide what is behind them
  const SOLID_FRONT = /^(door|slider|drawer-front)$/;
  const FRONT_PART = /^(door|door-stile|door-rail|glass|slider|drawer-front|drawer-side|drawer-rear|drawer-bottom)$/;
  const propsSig = (b: BuildModel) => {
    const W = b.envelope.W * MM,
      D = b.envelope.D * MM;
    return [b.openings.map((x) => `${Math.round(x.clearMm / 10)}:${Math.round((x.bounds.x1 - x.bounds.x0) / 10)}`).join(','), Math.round(W * 100), Math.round(D * 100), b.parts.filter((p) => SOLID_FRONT.test(p.role)).map((p) => p.id).join(',')].join('|');
  };

  function rebuildProps() {
    clearProps();
    propsSignature = '';
    if (!o.props || !build) return;
    // the carcass depth (the envelope grows by overlay doors); openings behind solid doors or drawers stay empty
    const D = Math.max(...build.parts.filter((p) => !FRONT_PART.test(p.role)).map((p) => p.placement.z + p.placement.sz), 0) * MM || build.envelope.D * MM,
      b = thicknessesOf(build).back * MM;
    propsSignature = propsSig(build);
    const m4 = new THREE.Matrix4(),
      q = new THREE.Quaternion(),
      s = new THREE.Vector3(),
      p = new THREE.Vector3(),
      col = new THREE.Color();
    const colour = (r: () => number) => BOOK_COLOURS[Math.floor(r() * BOOK_COLOURS.length)];
    const fronts = build.parts.filter((p) => SOLID_FRONT.test(p.role));
    // covered when solid fronts hide most of the opening (a pair of doors leaves only the reveal between its leaves)
    const hidden = (op: BuildModel['openings'][number]) => {
      const area = (op.bounds.x1 - op.bounds.x0) * (op.bounds.y1 - op.bounds.y0);
      const covered = fronts.reduce((s2, f) => {
        const q = f.placement;
        const ox = Math.min(q.x + q.sx, op.bounds.x1) - Math.max(q.x, op.bounds.x0), oy = Math.min(q.y + q.sy, op.bounds.y1) - Math.max(q.y, op.bounds.y0);
        return s2 + (ox > 0 && oy > 0 ? ox * oy : 0);
      }, 0);
      return area > 0 && covered / area > 0.5;
    };
    build.openings.forEach((op, i) => {
      if (hidden(op)) return;
      const clear = op.clearMm * MM;
      const inner = (op.bounds.x1 - op.bounds.x0) * MM;
      const r = rng(1000 + i * 77);
      const g = new THREE.Group();
      g.userData.opening = i;
      const kind = clear < 0.17 ? 'object' : ['row-left', 'row-right', 'stack', 'row-left', 'row-split'][i % 5];
      const books: Book[] = [];
      const bookDepth = () => Math.min(D - b - 0.03, 0.16 + r() * 0.07);
      const bookH = () => Math.min(clear - 0.025, 0.2 + r() * 0.1);
      if (kind.startsWith('row')) {
        const fillTo = inner * (0.2 + r() * 0.18);
        let x = kind === 'row-right' ? inner - 0.012 : 0.012;
        const dir = kind === 'row-right' ? -1 : 1;
        let used = 0;
        while (used < fillTo) {
          const w = 0.02 + r() * 0.026,
            h = bookH(),
            d = bookDepth();
          books.push({ x: x + (dir * w) / 2, y: h / 2, z: D - b - 0.028 - d / 2 + b, w, h, d, c: colour(r), tilt: 0 });
          x += dir * (w + 0.0015);
          used += w + 0.0015;
        }
        // a leaning book at the end of the row
        if (r() > 0.4) {
          const w = 0.02,
            h = bookH() * 0.95,
            d = bookDepth();
          const ang = 0.18 * dir;
          books.push({
            x: x + dir * ((Math.sin(Math.abs(ang)) * h) / 2 + w / 2),
            y: (Math.cos(ang) * h) / 2 + (Math.abs(Math.sin(ang)) * w) / 2,
            z: D - b - 0.03 - d / 2 + b, w, h, d, c: colour(r), tilt: ang,
          });
        }
        if (kind === 'row-split') {
          const vase = new THREE.Mesh(vaseGeo, ceramicMat);
          vase.userData.sharedGeo = true;
          vase.position.set(inner * 0.78, 0, D * 0.55);
          vase.castShadow = vase.receiveShadow = true;
          g.add(vase);
          propMeshes.push(vase);
        }
      } else if (kind === 'stack') {
        let y = 0;
        const n = 3 + Math.floor(r() * 3);
        const cx = inner * (0.22 + r() * 0.1);
        for (let k = 0; k < n && y < clear - 0.06; k++) {
          const h = 0.018 + r() * 0.03,
            w = 0.2 + r() * 0.06,
            d = Math.min(D - b - 0.04, 0.15 + r() * 0.06);
          books.push({ x: cx + (r() - 0.5) * 0.02, y: y + h / 2, z: D * 0.55, w, h, d, c: colour(r), tilt: 0, yaw: (r() - 0.5) * 0.12 });
          y += h;
        }
        const bowl = new THREE.Mesh(bowlGeo, ceramicDarkMat);
        bowl.userData.sharedGeo = true;
        bowl.scale.copy(bowlScale);
        bowl.position.set(inner * 0.72, 0, D * 0.55);
        bowl.castShadow = bowl.receiveShadow = true;
        g.add(bowl);
        propMeshes.push(bowl);
      } else if (clear > 0.1) {
        const bowl = new THREE.Mesh(bowlGeo, ceramicMat);
        bowl.userData.sharedGeo = true;
        bowl.scale.copy(bowlScale);
        bowl.position.set(inner * 0.5, 0, D * 0.55);
        bowl.castShadow = bowl.receiveShadow = true;
        g.add(bowl);
        propMeshes.push(bowl);
      }
      if (books.length) {
        const im = new THREE.InstancedMesh(unitBox, bookMat, books.length);
        books.forEach((bk, k) => {
          q.setFromEuler(new THREE.Euler(0, bk.yaw || 0, bk.tilt || 0));
          m4.compose(p.set(bk.x, bk.y, bk.z), q, s.set(bk.w, bk.h, bk.d));
          im.setMatrixAt(k, m4);
          im.setColorAt(k, col.set(bk.c));
        });
        im.castShadow = im.receiveShadow = true;
        im.userData.sharedGeo = true;
        g.add(im);
        propMeshes.push(im);
      }
      propsGroup.add(g);
      propMeshes.push(g);
    });
    placeProps();
  }

  function placeProps() {
    for (const g of propsGroup.children) {
      const i = g.userData.opening as number;
      const rect = openingRect(i);
      if (!rect) {
        g.visible = false;
        continue;
      }
      g.visible = explodeT < 0.01 && propsView !== 'edge-detail';
      g.position.set(rect.x0, rect.y0, 0);
    }
  }

  // --- views ------------------------------------------------------------------------
  const tmpCam = new THREE.PerspectiveCamera();
  function sceneBoxPoints(): THREE.Vector3[] {
    const { W, H, D } = frameDims();
    const xs = [-W / 2, W / 2],
      ys = [0, H],
      zs = [-D / 2, D / 2];
    const pts: THREE.Vector3[] = [];
    for (const x of xs) for (const y of ys) for (const z of zs) pts.push(new THREE.Vector3(x, y, z));
    if (o.scaleFigure) {
      const fx = -(W / 2 + 0.78);
      pts.push(new THREE.Vector3(fx - 0.25, 1.76, 0.1), new THREE.Vector3(fx - 0.25, 0, 0.1), new THREE.Vector3(fx + 0.25, 1.76, -0.1));
    }
    if (dimsOn()) {
      pts.push(new THREE.Vector3(-W / 2 - 0.12, 0, D / 2), new THREE.Vector3(W / 2 + 0.14, H, D / 2), new THREE.Vector3(0, -0.08, D / 2));
    }
    return pts;
  }
  function fitDistance(dir: THREE.Vector3, target: THREE.Vector3, aspect: number, fov: number, margin: number): number {
    tmpCam.fov = fov;
    tmpCam.aspect = aspect;
    tmpCam.near = 0.01;
    tmpCam.far = 100;
    tmpCam.updateProjectionMatrix();
    const pts = sceneBoxPoints();
    let lo = 0.2,
      hi = 30;
    const v = new THREE.Vector3();
    for (let it = 0; it < 26; it++) {
      const mid = (lo + hi) / 2;
      tmpCam.position.copy(target).addScaledVector(dir, mid);
      tmpCam.lookAt(target);
      tmpCam.updateMatrixWorld();
      let ok = true;
      for (const p of pts) {
        v.copy(p).project(tmpCam);
        if (Math.abs(v.x) > margin || Math.abs(v.y) > margin || v.z > 1) {
          ok = false;
          break;
        }
      }
      if (ok) hi = mid;
      else lo = mid;
    }
    return hi;
  }
  function viewPose(name: ViewName, aspect: number, azOffset = 0): { target: THREE.Vector3; position: THREE.Vector3; fov: number } {
    const { W, H, D, t } = frameDims();
    const fov = 30;
    const figShift = o.scaleFigure ? -0.5 : 0;
    const dirFrom = (azDeg: number, elDeg: number) => {
      const az = THREE.MathUtils.degToRad(azDeg + azOffset),
        el = THREE.MathUtils.degToRad(elDeg);
      return new THREE.Vector3(Math.sin(az) * Math.cos(el), Math.sin(el), Math.cos(az) * Math.cos(el));
    };
    if (name === 'edge-detail') {
      // top front-left corner: the top's ply edge butting into the left side
      const target = new THREE.Vector3(-W / 2 + t * 1.6, H - t * 0.8, D / 2 - 0.006);
      const dir = dirFrom(-36, 30).normalize();
      return { target, position: target.clone().addScaledVector(dir, 0.32), fov: 28 };
    }
    let dir: THREE.Vector3,
      target = new THREE.Vector3(figShift / 2, H / 2, 0),
      margin = aspect >= 1 ? 0.9 : 0.84; // tighter on wide stages; narrow ones keep room for dimension labels
    switch (name) {
      case 'front':
        dir = dirFrom(0, 4);
        margin = aspect >= 1 ? 0.92 : 0.86;
        break;
      case 'side':
        dir = dirFrom(88, 6);
        target = new THREE.Vector3(0, H / 2, 0);
        break;
      case 'top':
        dir = dirFrom(18, 62);
        target = new THREE.Vector3(figShift / 2, H * 0.4, 0);
        break;
      default:
        dir = dirFrom(34, 11);
        break;
    }
    dir.normalize();
    const dist = fitDistance(dir, target, aspect, fov, margin);
    return { target, position: target.clone().addScaledVector(dir, dist), fov };
  }
  function frameView(name: ViewName, { animate = true, duration = 750 }: { animate?: boolean; duration?: number } = {}) {
    const pose = viewPose(name, camera.aspect || 1);
    const from: CamPose = { p: camera.position.clone(), t: (controls ? controls.target : new THREE.Vector3(0, frameDims().H / 2, 0)).clone(), fov: camera.fov };
    if (!animate || reducedMotion()) {
      camTween = null;
      camera.position.copy(pose.position);
      camera.fov = pose.fov;
      camera.updateProjectionMatrix();
      if (controls) controls.target.copy(pose.target);
      camera.lookAt(pose.target);
      controls?.update();
    } else camTween = { t0: performance.now(), dur: duration, from, to: { p: pose.position, t: pose.target, fov: pose.fov } };
    invalidate();
  }
  /** A short arrival orbit on the first model in the three-quarter view, so the stage reads as live 3D. */
  function arrivalOrbit(): boolean {
    if (arrived || currentView !== 'three-quarter' || reducedMotion()) return false;
    arrived = true;
    const aspect = camera.aspect || 1;
    const start = viewPose('three-quarter', aspect, -7), end = viewPose('three-quarter', aspect);
    camera.position.copy(start.position);
    camera.fov = start.fov;
    camera.updateProjectionMatrix();
    if (controls) controls.target.copy(start.target);
    camera.lookAt(start.target);
    camTween = { t0: performance.now(), dur: 1600, from: { p: start.position.clone(), t: start.target.clone(), fov: start.fov }, to: { p: end.position, t: end.target, fov: end.fov } };
    invalidate();
    return true;
  }
  function setView(name: ViewName, { animate = true }: { animate?: boolean } = {}) {
    currentView = name;
    propsView = name;
    placeProps();
    userMoved = false;
    frameView(name, { animate });
  }

  // --- dimension overlay ----------------------------------------------------------------
  function dimsOn(): boolean {
    return !!o.showDims && !!build && !finished;
  }
  const v3 = new THREE.Vector3();
  function projectWorld(x: number, y: number, z: number) {
    v3.set(x, y, z).project(camera);
    return { x: ((v3.x + 1) / 2) * hostW, y: ((1 - v3.y) / 2) * hostH, behind: v3.z > 1 };
  }
  function project(x: number, y: number, z: number) {
    // carcass-local metres (origin floor / left / back) → host px
    const sd = stageDims();
    return projectWorld(x - sd.W / 2, y, z - sd.D / 2);
  }
  function drawDims() {
    if (!dimsOn() || explodeT > 0.01 || !build) {
      if (overlay.childNodes.length) overlay.replaceChildren();
      return;
    }
    const T = THEMES[theme] || THEMES.light;
    const parts: string[] = [];
    type Pt = { x: number; y: number };
    const fmt = (m: number) => {
      const v = Math.round(m * 10000) / 10;
      return Number.isInteger(v) ? String(v) : v.toFixed(1);
    };
    const line = (a: Pt, b2: Pt, w = 1, c = T.ink, op = 1) =>
      parts.push(`<line x1="${a.x.toFixed(1)}" y1="${a.y.toFixed(1)}" x2="${b2.x.toFixed(1)}" y2="${b2.y.toFixed(1)}" stroke="${c}" stroke-width="${w}" stroke-opacity="${op}"/>`);
    const label = (p: Pt, text: string, rot = 0, anchor = 'middle', size = 12, weight = 600) =>
      parts.push(
        `<text x="${p.x.toFixed(1)}" y="${p.y.toFixed(1)}" text-anchor="${anchor}" dominant-baseline="central" font-size="${size}" font-weight="${weight}" fill="${T.ink}" stroke="${T.halo}" stroke-width="3.5" paint-order="stroke" stroke-linejoin="round"${rot ? ` transform="rotate(${rot} ${p.x.toFixed(1)} ${p.y.toFixed(1)})"` : ''}>${text}</text>`,
      );
    const { W, H, D } = dims;
    const C = project(W / 2, H / 2, D);
    const offsetLine = (A: Pt, B: Pt, off: number, text: string, { size = 12, horizontal = false }: { size?: number; horizontal?: boolean } = {}) => {
      const dx = B.x - A.x,
        dy = B.y - A.y,
        len = Math.hypot(dx, dy) || 1;
      let nx = -dy / len,
        ny = dx / len;
      const mx = (A.x + B.x) / 2 - C.x,
        my = (A.y + B.y) / 2 - C.y;
      if (nx * mx + ny * my < 0) {
        nx = -nx;
        ny = -ny;
      }
      const a = { x: A.x + nx * off, y: A.y + ny * off },
        b2 = { x: B.x + nx * off, y: B.y + ny * off };
      line({ x: A.x + nx * 4, y: A.y + ny * 4 }, { x: a.x + nx * 4, y: a.y + ny * 4 }, 0.8, T.inkSoft);
      line({ x: B.x + nx * 4, y: B.y + ny * 4 }, { x: b2.x + nx * 4, y: b2.y + ny * 4 }, 0.8, T.inkSoft);
      line(a, b2, 1, T.ink, 0.9);
      const tick = (p: Pt) => line({ x: p.x - 3.5, y: p.y + 3.5 }, { x: p.x + 3.5, y: p.y - 3.5 }, 1.3);
      tick(a);
      tick(b2);
      if (len < 16) return;
      const mid = { x: (a.x + b2.x) / 2, y: (a.y + b2.y) / 2 };
      if (horizontal) {
        label({ x: mid.x + nx * 7, y: mid.y + ny * 7 }, text, 0, nx >= 0 ? 'start' : 'end', size);
      } else {
        let ang = (Math.atan2(dy, dx) * 180) / Math.PI;
        if (ang > 90) ang -= 180;
        if (ang < -90) ang += 180;
        label({ x: mid.x + nx * 11, y: mid.y + ny * 11 }, text, ang, 'middle', size);
      }
    };
    const fl = project(0, 0, D),
      fr = project(W, 0, D);
    if (fl.behind || fr.behind) {
      overlay.replaceChildren();
      return;
    }
    offsetLine(fl, fr, 22, fmt(W));
    // put the vertical chains beside the side panel that is facing away from the camera
    const leftFaceVisible = project(0, H / 2, 0).x < project(0, H / 2, D).x - 0.5;
    const xs = leftFaceVisible ? W : 0;
    const bl = project(xs, 0, D),
      tlx = project(xs, H, D);
    offsetLine(bl, tlx, 58, fmt(H));
    // the opening chain of the bay (column) on that side only: other bays would overlap it
    const edge = xs === 0 ? Math.min(...build.openings.map((o) => o.bounds.x0)) : Math.max(...build.openings.map((o) => o.bounds.x1));
    for (let i = 0; i < build.openings.length; i++) {
      const bd = build.openings[i].bounds;
      if ((xs === 0 ? bd.x0 : bd.x1) !== edge) continue;
      const r = openingRect(i);
      if (!r) continue;
      const a = project(xs, r.y0, D),
        b2 = project(xs, r.y1, D);
      offsetLine(a, b2, 18, fmt(build.openings[i].clearMm * MM), { size: 11, horizontal: true });
    }
    overlay.setAttribute('viewBox', `0 0 ${hostW} ${hostH}`);
    overlay.innerHTML = parts.join('');
  }

  // --- picking ------------------------------------------------------------------------
  const raycaster = new THREE.Raycaster();
  const ndc = new THREE.Vector2();
  function hitTest(clientX: number, clientY: number): { partId: string; opening: number | null } | null {
    const rect = canvas.getBoundingClientRect();
    ndc.set(((clientX - rect.left) / rect.width) * 2 - 1, -((clientY - rect.top) / rect.height) * 2 + 1);
    raycaster.setFromCamera(ndc, camera);
    if (finished) {
      const f = finished;
      for (const h of raycaster.intersectObjects(f.pickMeshes, false)) {
        const id = partIdOf(h.object, f.root);
        if (id) return { partId: id, opening: null };
      }
      return null;
    }
    const meshes = [...panels.values()].filter((r) => !r.removing).map((r) => r.mesh);
    const hit = raycaster.intersectObjects(meshes, false)[0];
    if (!hit) return null;
    const id = hit.object.userData.partId as string;
    if (id.startsWith('back') && build && explodeT < 0.01) {
      // inside an opening, seen from the front?
      const local = hit.point.clone().sub(carcass.position);
      if (local.z > dims.b * 0.5 && local.x > dims.t && local.x < dims.W - dims.t) {
        for (let i = 0; i < build.openings.length; i++) {
          const r = openingRect(i);
          if (r && local.y >= r.y0 && local.y <= r.y1 && local.x >= r.x0 && local.x <= r.x1) return { partId: id, opening: i };
        }
      }
    }
    return { partId: id, opening: null };
  }
  let downAt: { x: number; y: number } | null = null;
  const onPointerDown = (e: PointerEvent) => {
    if (e.button === 0) downAt = { x: e.clientX, y: e.clientY };
  };
  const onPointerUp = (e: PointerEvent) => {
    if (!downAt) return;
    const moved = Math.hypot(e.clientX - downAt.x, e.clientY - downAt.y);
    downAt = null;
    if (moved > 5) return;
    const h = hitTest(e.clientX, e.clientY);
    if (h && h.opening != null && o.onOpeningPick) {
      o.onOpeningPick(h.opening, e.shiftKey);
      return;
    }
    const id = h ? h.partId : null;
    select(id);
    o.onPick?.(id);
  };
  let hoverQueued: { x: number; y: number } | null = null;
  const onPointerMove = (e: PointerEvent) => {
    if (e.pointerType === 'touch' || downAt) return;
    hoverQueued = { x: e.clientX, y: e.clientY };
  };
  const onPointerLeave = () => {
    hoverQueued = null;
    setHover(null, null, true);
  };
  function processHover() {
    if (!hoverQueued) return;
    const { x, y } = hoverQueued;
    hoverQueued = null;
    const h = hitTest(x, y);
    if (h && h.opening != null && (o.onOpeningPick || o.onOpeningHover)) setHover(null, h.opening, true);
    else setHover(h ? h.partId : null, null, true);
    canvas.style.cursor = h ? 'pointer' : '';
  }
  function setHover(id: string | null, opening: number | null, fromPointer: boolean) {
    const changedPart = id !== hovered,
      changedOpening = opening !== openingHover;
    if (!changedPart && !changedOpening) return;
    hovered = id;
    openingHover = opening;
    refreshMaterials();
    placeHighlights();
    if (fromPointer) {
      if (changedPart) o.onHover?.(id);
      if (changedOpening) o.onOpeningHover?.(opening);
    }
  }
  if (o.interactive) {
    canvas.addEventListener('pointerdown', onPointerDown);
    canvas.addEventListener('pointerup', onPointerUp);
    canvas.addEventListener('pointermove', onPointerMove);
    canvas.addEventListener('pointerleave', onPointerLeave);
  }

  function select(id: string | null) {
    selected = id && hasPart(id) ? id : null;
    refreshMaterials();
    placeHighlights();
  }
  function hover(id: string | null) {
    setHover(id && hasPart(id) ? id : null, openingHover, false);
  }
  function highlightOpening(i: number | null) {
    openingHL = i ?? null;
    placeHighlights();
    invalidate();
  }

  // --- theme ---------------------------------------------------------------------------
  function accentColour(): THREE.Color {
    return new THREE.Color(o.accent || THEMES[theme].accent);
  }
  function themeHighlight(m: THREE.Material, kind: HighlightKind) {
    if (!(m instanceof THREE.MeshStandardMaterial)) return;
    if (kind === 'hover') {
      m.emissive.set(0xfff1dc);
      m.emissiveIntensity = theme === 'dark' ? 0.05 : 0.06;
    } else {
      m.emissive.copy(accentColour());
      m.emissiveIntensity = theme === 'dark' ? 0.08 : 0.06;
    }
  }
  function setTheme(name: ThemeName) {
    theme = isThemeName(name) ? name : 'light';
    o.theme = theme;
    const T = THEMES[theme];
    renderer.toneMappingExposure = T.exposure;
    scene.environmentIntensity = T.env;
    key.intensity = T.key;
    key.color.set(T.keyColour);
    fill.intensity = T.fill;
    fill.color.set(T.fillColour);
    floorMat.opacity = T.shadow;
    contactMat.opacity = T.contact;
    glowMat.opacity = T.glow;
    glowMat.color.set(T.glowColour);
    figureMat.color.set(T.figure);
    wallMat.color.set(T.wall);
    roomFloorMat.color.set(T.floor);
    const acc = accentColour();
    for (const m of [highlightMat, highlightLineMat, outlineMat, selShellMat, hovShellMat]) m.color.copy(acc);
    for (const v of variants.values()) for (const k of ['hover', 'selected'] as const) if (v[k]) themeHighlight(v[k], k);
    if (finished) for (const v of finished.variants.values()) for (const k of ['hover', 'selected'] as const) if (v[k]) themeHighlight(v[k], k);
    applyBackground();
    invalidate();
  }
  function applyBackground(bg = o.background) {
    if (!bg || bg === 'transparent') {
      renderer.setClearColor(0x000000, 0);
      scene.background = null;
    } else {
      renderer.setClearColor(new THREE.Color(bg), 1);
    }
  }

  function setOptions(next: Partial<ViewerOptions> = {}) {
    const before = { ...o };
    Object.assign(o, next);
    if ('scaleFigure' in next) {
      figure.visible = !!o.scaleFigure;
      placeStage();
      if (!userMoved) frameView(currentView, { animate: true, duration: 500 });
    }
    if ('props' in next && before.props !== o.props) rebuildProps();
    if ('exploded' in next && before.exploded !== o.exploded) {
      explodeFrom = explodeT;
      explodeTo = o.exploded ? 1 : 0;
      for (const r of panels.values()) {
        r.from = r.cur;
        r.to = r.to || r.cur;
      }
      fromDims = { ...dims };
      rebuildAllGeometry(); // Inspect uses the kit's Inspect LOD
      scheduleLayer(0);
      if (reducedMotion()) step(1);
      else anim = { t0: performance.now(), dur: 420 };
    }
    if ('frontsOpen' in next && !!before.frontsOpen !== !!o.frontsOpen) {
      openFrom = openT;
      openTo = o.frontsOpen ? 1 : 0;
      explodeFrom = explodeT; explodeTo = explodeT;
      for (const r of panels.values()) { r.from = r.cur; r.to = r.to || r.cur; }
      fromDims = { ...dims };
      if (reducedMotion()) step(1);
      else anim = { t0: performance.now(), dur: 700 };
    }
    if ('detail' in next && !!before.detail !== !!o.detail) {
      // Inspect on the assembled model: the kit's Inspect LOD without pulling the parts apart
      rebuildAllGeometry();
      scheduleLayer(0);
      invalidate();
    }
    if ('appearance' in next && JSON.stringify(before.appearance ?? null) !== JSON.stringify(o.appearance ?? null)) {
      const finishChanged = (before.appearance?.finishId ?? null) !== (o.appearance?.finishId ?? null);
      refreshMaterials();
      if (finishChanged) scheduleLayer(0);
      else placeLayer();
    }
    if ('showDims' in next && !userMoved) frameView(currentView, { animate: true, duration: 400 });
    if ('background' in next) applyBackground();
    if ('accent' in next || 'theme' in next) setTheme(o.theme);
    if ('backdrop' in next) {
      backdrop.visible = !!o.backdrop;
      floor.visible = !o.backdrop;
    }
    if ('inset' in next) {
      const nextInset = normInset(next.inset);
      if (JSON.stringify(nextInset) !== JSON.stringify(inset)) {
        inset = nextInset;
        if (!snapshotting) {
          applyInset();
          if (!userMoved && (build || finished)) frameView(currentView, { animate: true, duration: 400 });
        }
      }
    }
    invalidate();
  }

  // --- size --------------------------------------------------------------------------
  function applyInset() {
    const l = inset.left,
      r = inset.right,
      t = inset.top,
      b = inset.bottom;
    const iw = Math.max(40, hostW - l - r),
      ih = Math.max(40, hostH - t - b);
    camera.aspect = iw / ih;
    if (l || r || t || b) camera.setViewOffset(iw, ih, -l, -t, hostW, hostH);
    else camera.clearViewOffset();
    camera.updateProjectionMatrix();
  }
  function resize() {
    if (snapshotting) {
      resizePending = true;
      return;
    }
    const r = host.getBoundingClientRect();
    hostW = Math.max(1, Math.round(r.width));
    hostH = Math.max(1, Math.round(r.height));
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(hostW, hostH, false);
    applyInset();
    if (!userMoved && (build || finished)) frameView(currentView, { animate: false });
    invalidate();
  }
  const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(() => resize()) : null;
  ro?.observe(host);

  // --- render loop ----------------------------------------------------------------------
  function render() {
    const t0 = performance.now();
    renderer.render(scene, camera);
    lastFrameMs = performance.now() - t0;
  }
  function tick() {
    raf = requestAnimationFrame(tick);
    if (lost || disposed || snapshotting) return;
    const now = performance.now();
    processHover();
    if (anim) {
      const k = (now - anim.t0) / anim.dur;
      step(k);
      if (k >= 1) {
        anim = null;
        if (propsNeedRebuild()) rebuildProps();
      }
      needsRender = true;
    }
    if (camTween) {
      const k = clamp01((now - camTween.t0) / camTween.dur),
        e = easeInOut(k);
      camera.position.lerpVectors(camTween.from.p, camTween.to.p, e);
      camera.fov = camTween.from.fov + (camTween.to.fov - camTween.from.fov) * e;
      camera.updateProjectionMatrix();
      const tgt = new THREE.Vector3().lerpVectors(camTween.from.t, camTween.to.t, e);
      if (controls) controls.target.copy(tgt);
      camera.lookAt(tgt);
      if (k >= 1) camTween = null;
      needsRender = true;
      o.onCameraChange?.();
    }
    if (controls && controls.update()) needsRender = true;
    if (!needsRender) return;
    needsRender = false;
    render();
    drawDims();
    for (const fn of frameSubs) {
      try {
        fn();
      } catch (err) {
        console.error(err);
      }
    }
  }
  function propsNeedRebuild(): boolean {
    if (!o.props || !build) return false;
    return propsSig(build) !== propsSignature;
  }

  // --- context loss -----------------------------------------------------------------------
  const onLost = (e: Event) => {
    e.preventDefault();
    lost = true;
    notice.textContent = '3D preview paused while the graphics context restarts…';
    notice.style.display = 'block';
    o.onContextLost?.(true);
  };
  const onRestored = () => {
    lost = false;
    notice.style.display = 'none';
    envRT = null; // its GL objects died with the old context; never delete them in the new one
    makeEnv();
    for (const t of [contactTex, glowTex, cornerTex, ...(kit ? kit.listTextures() : []), ...(finished ? finished.textures : [])]) t.needsUpdate = true;
    invalidate();
    o.onContextLost?.(false);
  };
  canvas.addEventListener('webglcontextlost', onLost);
  canvas.addEventListener('webglcontextrestored', onRestored);

  // --- screen positions --------------------------------------------------------------------
  function tallestOpening(): OpeningRect | null {
    if (!build) return null;
    let best: OpeningRect | null = null;
    build.openings.forEach((_op, i) => {
      const rr = openingRect(i);
      if (rr && (!best || rr.y1 - rr.y0 > best.y1 - best.y0)) best = rr;
    });
    return best;
  }
  function toScreen(p: { x: number; y: number; behind: boolean }): ScreenPosition {
    return { x: p.x, y: p.y, visible: !p.behind && p.x >= 0 && p.y >= 0 && p.x <= hostW && p.y <= hostH };
  }
  function getScreenPosition(targetSpec: ScreenTarget): ScreenPosition | null {
    if (finished && typeof targetSpec === 'string') {
      const box = finished.boxes.get(targetSpec);
      if (!box) return null;
      const c = box.getCenter(new THREE.Vector3());
      if (finished.roles.get(targetSpec) === 'back' || targetSpec === 'back') {
        // a visible spot on the back: centre of the tallest opening
        const best = tallestOpening();
        if (best) c.y = (best.y0 + best.y1) / 2;
      }
      return toScreen(projectWorld(c.x, c.y, box.max.z));
    }
    if (!build) return null;
    let x: number, y: number, z: number;
    if (typeof targetSpec === 'string') {
      const r = panels.get(targetSpec);
      if (!r?.cur) return null;
      const ex = explodeFor(r.id);
      x = r.cur.x + r.cur.sx / 2 + ex[0] * explodeT;
      y = r.cur.y + r.cur.sy / 2 + ex[1] * explodeT;
      z = r.cur.z + r.cur.sz + ex[2] * explodeT;
      if (r.part?.role === 'back') {
        const best = tallestOpening();
        if (best) y = (best.y0 + best.y1) / 2;
      }
    } else if (targetSpec && 'opening' in targetSpec && targetSpec.opening != null) {
      const rr = openingRect(targetSpec.opening);
      if (!rr) return null;
      x = (rr.x0 + rr.x1) / 2;
      y = (rr.y0 + rr.y1) / 2;
      z = rr.z1;
    } else if (targetSpec && 'point' in targetSpec && targetSpec.point) {
      x = targetSpec.point.x * MM;
      y = targetSpec.point.y * MM;
      z = targetSpec.point.z * MM;
    } else return null;
    return toScreen(project(x, y, z));
  }

  // --- finished model (GLB from the Blender pipeline) ------------------------------------------
  function finishedVariant(f: FinishedState, m: THREE.Material, kind: HighlightKind): THREE.Material {
    let entry = f.variants.get(m);
    if (!entry) {
      entry = {};
      f.variants.set(m, entry);
    }
    let v = entry[kind];
    if (!v) {
      v = m.clone();
      v.name = `${m.name}-${kind === 'selected' ? 'sel' : 'hover'}`;
      themeHighlight(v, kind);
      entry[kind] = v;
    }
    return v;
  }
  function refreshFinishedMaterials() {
    const f = finished;
    if (!f) return;
    for (const [id, meshes] of f.partMeshes) {
      const kind: HighlightKind | null = id === selected ? 'selected' : id === hovered ? 'hover' : null;
      for (const mesh of meshes) {
        const orig = f.original.get(mesh);
        if (!orig) continue;
        mesh.material = !kind ? orig : Array.isArray(orig) ? orig.map((m) => finishedVariant(f, m, kind)) : finishedVariant(f, orig, kind);
      }
    }
  }
  function teardownFinished() {
    const f = finished;
    if (!f) return;
    finished = null;
    scene.remove(f.root);
    for (const [mesh, orig] of f.original) mesh.material = orig;
    for (const entry of f.variants.values()) for (const v of Object.values(entry)) v?.dispose();
    disposeObject(f.root);
  }
  function showLive() {
    carcass.visible = true;
    finishedHL.visible = false;
    if (selected && !panels.has(selected)) selected = null;
    if (hovered && !panels.has(hovered)) hovered = null;
  }

  async function loadFinishedModel(url: string): Promise<FinishedModelInfo> {
    if (disposed) throw new Error('Viewer is disposed');
    const seq = ++loadSeq;
    const gltf = await new GLTFLoader().loadAsync(url);
    const root = gltf.scene;
    if (disposed || seq !== loadSeq) {
      disposeObject(root);
      throw new Error(disposed ? 'Viewer was disposed while the finished model loaded' : 'Finished model load was superseded');
    }
    const nodes = collectPartNodes(root);
    if (!nodes.partIds.length) {
      disposeObject(root);
      throw new Error('The finished model has no part IDs: no GLB node carries extras.partId.');
    }
    const wrapper = new THREE.Group();
    wrapper.name = 'finished-model';
    wrapper.add(root);
    for (const s of nodes.staging) s.visible = false;
    const partMeshes = new Map<string, THREE.Mesh[]>();
    const pickMeshes: THREE.Mesh[] = [];
    const original = new Map<THREE.Mesh, THREE.Material | THREE.Material[]>();
    const textures = new Set<THREE.Texture>();
    const roles = new Map<string, string>();
    for (const [id, list] of nodes.parts) {
      const meshes: THREE.Mesh[] = [];
      for (const node of list) {
        if (typeof node.userData.role === 'string' && !roles.has(id)) roles.set(id, node.userData.role);
        eachMesh(node, (mesh) => {
          mesh.castShadow = true;
          mesh.receiveShadow = true;
          meshes.push(mesh);
          const mat = mesh.material as THREE.Material | THREE.Material[];
          original.set(mesh, mat);
          for (const m of materialList(mat)) for (const t of texturesOf(m)) textures.add(t);
        });
      }
      partMeshes.set(id, meshes);
      pickMeshes.push(...meshes);
    }
    // centre on x/z and stand on y = 0, measured from the part nodes only
    wrapper.updateMatrixWorld(true);
    const box = new THREE.Box3();
    for (const meshes of partMeshes.values()) for (const m of meshes) box.expandByObject(m);
    if (box.isEmpty()) {
      disposeObject(wrapper);
      throw new Error('The finished model has part IDs but no part geometry.');
    }
    const centre = box.getCenter(new THREE.Vector3());
    wrapper.position.set(-centre.x, -box.min.y, -centre.z);
    wrapper.updateMatrixWorld(true);
    const boxes = new Map<string, THREE.Box3>();
    for (const [id, meshes] of partMeshes) {
      const b = new THREE.Box3();
      for (const m of meshes) b.expandByObject(m);
      if (!b.isEmpty()) boxes.set(id, b);
    }
    const size = box.getSize(new THREE.Vector3());

    teardownFinished(); // replacing an earlier finished model
    scene.add(wrapper);
    const info: FinishedModelInfo = { partIds: nodes.partIds, duplicates: nodes.duplicates };
    finished = {
      root: wrapper, partMeshes, pickMeshes, original, variants: new Map(), boxes, roles, textures: [...textures], info,
      dims: {
        W: size.x, H: size.y, D: size.z,
        t: build ? thicknessesOf(build).carcass * MM : dims.t,
        b: build ? thicknessesOf(build).back * MM : dims.b,
      },
    };
    carcass.visible = false;
    finishedHL.visible = true;
    overlay.replaceChildren();
    if (selected && !hasPart(selected)) selected = null;
    if (hovered && !hasPart(hovered)) hovered = null;
    openingHover = null;
    refreshMaterials();
    placeStage();
    placeHighlights();
    userMoved = false;
    camTween = null;
    frameView(currentView, { animate: true });
    invalidate();
    return { partIds: [...info.partIds], duplicates: [...info.duplicates] };
  }

  function unloadFinishedModel() {
    loadSeq++; // supersede a pending load
    if (!finished) return;
    teardownFinished();
    showLive();
    refreshMaterials();
    placeStage();
    placeHighlights();
    placeProps();
    userMoved = false;
    camTween = null;
    if (build) frameView(currentView, { animate: true });
    invalidate();
  }

  // --- snapshot ------------------------------------------------------------------------------
  // Snapshots are serialised; the live loop pauses while one runs, and finished-quality
  // passes yield to the browser between passes so the rest of the UI keeps animating.
  let snapChain: Promise<unknown> = Promise.resolve();
  const yieldFrame = () =>
    new Promise<void>((r) => {
      let done = false;
      const go = () => {
        if (!done) {
          done = true;
          r();
        }
      };
      requestAnimationFrame(go);
      setTimeout(go, 50);
    });
  function snapshot(opts: SnapshotOptions = {}): Promise<string> {
    const run = snapChain.then(() => snapshotNow(opts));
    snapChain = run.catch(() => {});
    return run;
  }
  async function snapshotNow({ width = 1600, height = 1000, quality = 'preview', view = null, background, backdrop: bd, mime = 'image/png', samples }: SnapshotOptions = {}): Promise<string> {
    await ready;
    if (lost) throw new Error('Graphics context lost');
    if (disposed) throw new Error('Viewer is disposed');
    // settle animations
    if (anim) {
      step(1);
      anim = null;
    }
    if (camTween) camTween = null;
    const isFinished = quality === 'finished';
    const scale = isFinished ? (software ? 1 : 1.5) : 1;
    const rw = Math.round(width * scale),
      rh = Math.round(height * scale);
    const saved = {
      pr: renderer.getPixelRatio(), w: hostW, h: hostH, pos: camera.position.clone(), quat: camera.quaternion.clone(), fov: camera.fov, aspect: camera.aspect,
      target: controls ? controls.target.clone() : null, key: key.position.clone(), map: key.shadow.mapSize.clone(),
      backdrop: backdrop.visible, floor: floor.visible, exposure: renderer.toneMappingExposure, clear: renderer.getClearColor(new THREE.Color()), clearA: renderer.getClearAlpha(),
      fillI: fill.intensity, env: scene.environmentIntensity, glow: glowMat.opacity, contact: contactMat.opacity,
    };
    const useBackdrop = bd ?? isFinished;
    snapshotting = true;
    try {
      camera.clearViewOffset();
      snapshotQuality = quality;
      renderer.setPixelRatio(1);
      renderer.setSize(rw, rh, false);
      camera.aspect = rw / rh;
      camera.updateProjectionMatrix();
      const snapView = view || (!userMoved ? currentView : null);
      propsView = snapView || currentView;
      placeProps();
      if (snapView) {
        const pose = viewPose(snapView, rw / rh);
        camera.position.copy(pose.position);
        camera.fov = pose.fov;
        camera.updateProjectionMatrix();
        camera.lookAt(pose.target);
      } else if (controls) camera.lookAt(controls.target);
      backdrop.visible = useBackdrop;
      floor.visible = !useBackdrop;
      if (useBackdrop) glowMat.opacity = theme === 'dark' ? THEMES.dark.glow * 0.6 : 0;
      if (background && background !== 'transparent') renderer.setClearColor(new THREE.Color(background), 1);
      else if (useBackdrop) renderer.setClearColor(new THREE.Color(THEMES[theme].wall), 1);
      else if (background === 'transparent') renderer.setClearColor(0, 0);
      step(1); // pushes AO strength for the quality
      const out = document.createElement('canvas');
      out.width = width;
      out.height = height;
      const ctx = out.getContext('2d');
      if (!ctx) throw new Error('2D canvas is not available');
      if (isFinished) {
        fill.intensity = saved.fillI * 1.4;
        scene.environmentIntensity = saved.env * 0.62;
        key.intensity = 0;
        key.castShadow = false;
        spot.visible = true;
        spot.intensity = THEMES[theme].key * 1.7;
        spot.color.copy(key.color);
        renderer.toneMappingExposure = saved.exposure * 1.04;
        const n = samples ?? (software ? 5 : 16);
        const base = spot.position.clone();
        const toLight = base.clone().sub(spot.target.position).normalize();
        const u = new THREE.Vector3().crossVectors(toLight, new THREE.Vector3(0, 1, 0)).normalize();
        const v = new THREE.Vector3().crossVectors(u, toLight).normalize();
        const acc = document.createElement('canvas');
        acc.width = rw;
        acc.height = rh;
        const actx = acc.getContext('2d');
        if (!actx) throw new Error('2D canvas is not available');
        for (let i = 0; i < n; i++) {
          if (i > 0) await yieldFrame();
          if (disposed || lost) throw new Error('Viewer was disposed or lost its context during a snapshot');
          // area light: jitter the key on a disc (Vogel spiral), and the camera by sub-pixels
          const r = Math.sqrt((i + 0.5) / n) * 0.75,
            a = i * 2.39996;
          spot.position.copy(base).addScaledVector(u, Math.cos(a) * r).addScaledVector(v, Math.sin(a) * r);
          camera.setViewOffset(rw, rh, (((i * 0.618) % 1) - 0.5) * 0.9, (((i * 0.382 + 0.25) % 1) - 0.5) * 0.9, rw, rh);
          renderer.render(scene, camera);
          actx.globalAlpha = 1 / (i + 1);
          actx.drawImage(canvas, 0, 0);
        }
        camera.clearViewOffset();
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(acc, 0, 0, width, height);
        // gentle vignette
        const vg = ctx.createRadialGradient(width / 2, height * 0.48, Math.min(width, height) * 0.35, width / 2, height / 2, Math.hypot(width, height) * 0.62);
        vg.addColorStop(0, 'rgba(0,0,0,0)');
        vg.addColorStop(1, theme === 'dark' ? 'rgba(0,0,0,.38)' : 'rgba(40,28,14,.16)');
        ctx.globalCompositeOperation = 'source-atop';
        ctx.fillStyle = vg;
        ctx.fillRect(0, 0, width, height);
        ctx.globalCompositeOperation = 'source-over';
      } else {
        renderer.render(scene, camera);
        ctx.drawImage(canvas, 0, 0, width, height);
      }
      return out.toDataURL(mime, 0.92);
    } finally {
      snapshotQuality = 'preview';
      propsView = currentView;
      key.position.copy(saved.key);
      if (key.shadow.mapSize.x !== saved.map.x) {
        key.shadow.mapSize.copy(saved.map);
        key.shadow.map?.dispose();
        key.shadow.map = null;
      }
      backdrop.visible = saved.backdrop;
      floor.visible = saved.floor;
      renderer.toneMappingExposure = saved.exposure;
      renderer.setClearColor(saved.clear, saved.clearA);
      spot.visible = false;
      spot.intensity = 0;
      key.castShadow = true;
      fill.intensity = saved.fillI;
      scene.environmentIntensity = saved.env;
      key.intensity = THEMES[theme].key;
      glowMat.opacity = saved.glow;
      contactMat.opacity = saved.contact;
      if (!disposed) {
        renderer.setPixelRatio(saved.pr);
        renderer.setSize(saved.w, saved.h, false);
      }
      camera.position.copy(saved.pos);
      camera.quaternion.copy(saved.quat);
      camera.fov = saved.fov;
      snapshotting = false;
      applyInset();
      step(1);
      invalidate();
      if (resizePending && !disposed) {
        resizePending = false;
        resize();
      }
    }
  }

  // --- dispose ---------------------------------------------------------------------------------
  function dispose() {
    if (disposed) return;
    disposed = true;
    loadSeq++;
    cancelAnimationFrame(raf);
    ro?.disconnect();
    canvas.removeEventListener('pointerdown', onPointerDown);
    canvas.removeEventListener('pointerup', onPointerUp);
    canvas.removeEventListener('pointermove', onPointerMove);
    canvas.removeEventListener('pointerleave', onPointerLeave);
    canvas.removeEventListener('webglcontextlost', onLost);
    canvas.removeEventListener('webglcontextrestored', onRestored);
    controls?.dispose();
    teardownFinished();
    clearProps();
    for (const r of panels.values()) r.geo.dispose();
    panels.clear();
    for (const g of figureGeos) g.dispose();
    for (const g of [floorGeo, unitPlane, unitBox, unitEdges, vaseGeo, bowlGeo, wallContactGeo]) g.dispose();
    const allMats = new Set<THREE.Material>([
      selShellMat, hovShellMat, wallContactMat, cornerMat, floorMat, contactMat, glowMat, wallMat, roomFloorMat, figureMat,
      highlightMat, highlightLineMat, outlineMat, bookMat, ceramicMat, ceramicDarkMat,
      ...fallbackMats.values(), ...[...variants.values()].flatMap((v) => Object.values(v)),
    ]);
    for (const m of allMats) m.dispose();
    if (layerTimer) clearTimeout(layerTimer);
    layer?.dispose();
    kit?.dispose();
    for (const t of [contactTex, glowTex, cornerTex]) t.dispose();
    envRT?.dispose();
    renderer.dispose();
    renderer.forceContextLoss();
    canvas.remove();
    overlay.remove();
    notice.remove();
    frameSubs.clear();
  }

  // --- boot ---------------------------------------------------------------------------------
  setTheme(theme);
  figure.visible = !!o.scaleFigure;
  if (o.backdrop) {
    backdrop.visible = true;
    floor.visible = false;
  }
  resize();
  raf = requestAnimationFrame(tick);
  void ready.then(() => o.onReady?.());

  return {
    update, select, hover, highlightOpening, setView, setTheme, setOptions, snapshot, resize, dispose, getScreenPosition,
    loadFinishedModel, unloadFinishedModel,
    onFrame(fn: () => void) {
      frameSubs.add(fn);
      return () => {
        frameSubs.delete(fn);
      };
    },
    get finishedModelLoaded() {
      return finished != null;
    },
    get canvas() {
      return canvas;
    },
    get lastFrameMs() {
      return lastFrameMs;
    },
    get lastUpdateMs() {
      return lastUpdateMs;
    },
    get view() {
      return currentView;
    },
    get selected() {
      return selected;
    },
    get hovered() {
      return hovered;
    },
    get textureSource() {
      return textureSource;
    },
    get theme() {
      return theme;
    },
    ready,
    _debug: { scene, camera, renderer, panels, controls, layer: layerGroup },
  };
}
